/* static/js/live_v2/phone_layout.js — how a custom board reads on a phone.
 *
 * Phase 5 of the phone work (Xav 2026-09-11). A user board has ONE desktop
 * layout per tab (sections[].panels, a 12-col grid) and, beside it, an
 * optional phone layout per tab:
 *
 *     dashboard.phone = { <section_id>: { panels: [{ id, span: 'full'|'half' }] } }
 *
 * Nobody has to design twice, so a tab with no phone entry is AUTOMATIC:
 * the desktop panels in reading order (top to bottom, then left to right),
 * narrow ones (under half the grid) paired two-up, and the charts plus the
 * endless all-players list left off - they're unreadable at phone width.
 * The first change in the editor's Phone canvas saves an entry and the tab
 * becomes the user's own.
 *
 * The phone layout only ever REFERENCES desktop panels, so a panel removed on
 * desktop drops off the phone too, and one added later waits in "not on the
 * phone" until it's added. The desktop layout is never written from here -
 * everything returns new objects.
 *
 * Pure: no DOM, no Alpine. Unit-tested in tests/js/phone_layout.test.js.
 */
window.LSPhoneLayout = (function () {
  'use strict';

  // Off the phone by default. Charts can't be read at 390px and All Players
  // is an endless scroll. Also the demo's phone curation list (_mobileCurate).
  const HIDE = [
    'all_players', 'lineup_race', 'stack_performance',
    'player_heatmap', 'pace_tracker', 'lineup_scores',
    'player_contributions',
  ];
  const HIDE_SET = new Set(HIDE);

  // KPI tiles on a phone: two across, two rows. More turns the top of the
  // screen into a wall of numbers before the first panel.
  const MAX_KPIS = 4;

  const SPANS = ['full', 'half'];

  function _panels(list) { return Array.isArray(list) ? list.filter(p => p && p.id) : []; }

  /** Desktop panels in reading order: row first, then column. */
  function readingOrder(panels) {
    return _panels(panels).slice().sort((a, b) =>
      ((a.y | 0) - (b.y | 0)) || ((a.x | 0) - (b.x | 0)));
  }

  /** Automatic phone list for a desktop tab. */
  function derive(panels) {
    return readingOrder(panels)
      .filter(p => !HIDE_SET.has(p.id))
      .map(p => ({ id: p.id, span: (p.w | 0) >= 6 ? 'full' : 'half' }));
  }

  /** True when this tab has a saved phone layout (vs automatic). */
  function isCustom(entry) {
    return !!(entry && Array.isArray(entry.panels));
  }

  /** The phone list actually shown: the saved one (minus panels that no longer
   *  exist on desktop, and any duplicates), else the automatic one. */
  function resolve(panels, entry) {
    if (!isCustom(entry)) return derive(panels);
    const onDesk = new Set(_panels(panels).map(p => p.id));
    const seen = new Set();
    const out = [];
    for (const p of entry.panels) {
      if (!p || !onDesk.has(p.id) || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push({ id: p.id, span: SPANS.includes(p.span) ? p.span : 'full' });
    }
    return out;
  }

  /** Desktop panels that aren't on the phone, in reading order. */
  function offPhone(panels, list) {
    const on = new Set((list || []).map(p => p.id));
    return readingOrder(panels).filter(p => !on.has(p.id)).map(p => p.id);
  }

  /** The layout the phone renders: same widgets + options, panels replaced by
   *  the phone list. Array order IS the stack order (cellOrder reads it) and
   *  w carries the width: 12 full, HALF_W half. HALF_W is under 6 because the
   *  iPad-upright 2-up projection (_tabletPlacements) reads w >= 6 as a full
   *  row and anything narrower as half; the phone reads w < 12 as half. h
   *  keeps the desktop height (the iPad pairs span the same rows). */
  const HALF_W = 4;
  function project(layout, entry) {
    const src = layout || {};
    const byId = new Map(_panels(src.panels).map(p => [p.id, p]));
    const panels = resolve(src.panels, entry).map((p, i) => ({
      id: p.id, x: 0, y: i,
      w: p.span === 'half' ? HALF_W : 12,
      h: Math.max(1, (byId.get(p.id).h | 0) || 3),
    }));
    return { ...src, panels, kpis: (src.kpis || []).slice(0, MAX_KPIS) };
  }

  return { HIDE, MAX_KPIS, readingOrder, derive, isCustom, resolve, offPhone, project };
})();
